/**
 * @file: useInfiniteListWithCursor.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { CursorResult } from '@euler/model/common';
import { uniq } from '@euler/utils/array';
import { usePersistFn } from '@euler/utils/hooks';
import produce, { Draft } from 'immer';
import { useRef, useState } from 'react';

export type InfiniteListStatusWithCursor<TNextKey> = {
  isRefreshing: boolean;
  isLoading: boolean;
  error: Error | undefined;
  hasMore: boolean;
  loadedDataLength: number;
  nextKey: TNextKey | undefined;
  limit: number;
};

export type InfiniteListStateWithCursor<T, TNextKey, U = any> = {
  fetch: (...params: U[]) => Promise<void>;
  fetchWithoutRefresh: (...params: U[]) => Promise<void>;
  replace: (...params: U[]) => Promise<void>;
  fetchMore: (...params: U[]) => Promise<void>;
  fetchSinceLastError: (...params: U[]) => Promise<void>;
  data: T[];
  status: InfiniteListStatusWithCursor<TNextKey>;
  update: (handler: (item: Draft<T>) => undefined | 'remove') => void;
};

const combineData = <T>(
  current: T[],
  next: T[],
  keyExtractor?: (item: T) => string,
): T[] => {
  if (keyExtractor == null) {
    return current.concat(next);
  }
  return uniq(current.concat(next), keyExtractor);
};

export const useInfiniteListWithCursor = <T, TNextKey, U = any>({
  limit,
  query,
  initialLoading,
  keyExtractor,
}: {
  query: (
    nextKey: TNextKey | undefined,
    ...params: U[]
  ) => Promise<CursorResult<T, TNextKey>>;
  limit: number;
  initialLoading?: boolean;
  keyExtractor?: (item: T) => string;
}): InfiniteListStateWithCursor<T, TNextKey, U> => {
  const [state, setState] = useState<{
    loading: boolean;
    error?: Error;
    data: T[];
    lastKey?: TNextKey | null;
  }>({
    loading: initialLoading ?? false,
    error: undefined,
    lastKey: null,
    data: [],
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [nextKey, setNextKey] = useState<TNextKey>();
  const [hasMore, setHasMore] = useState(true);
  const requestId = useRef<number>();

  const queryData = usePersistFn(
    async (
      params: U[],
      currentData: T[],
      cursor: TNextKey | undefined,
      mergeData: (current: T[], next: T[]) => T[],
      refresh = false,
      nextRequestId: number | undefined = undefined,
    ) => {
      if (refresh) {
        setIsRefreshing(true);
      }
      setState({ loading: true, error: undefined, data: currentData });
      try {
        const next = await query(cursor, ...params);

        // discard previous request results.
        if (nextRequestId != null && nextRequestId !== requestId.current) {
          return;
        }

        const data = mergeData(currentData, next.data);
        setState({ loading: false, error: undefined, data });
        setNextKey(next.cursor);
        setHasMore(next.cursor != null);
      } catch (e) {
        console.error(e);
        setState({
          loading: false,
          error: e as Error,
          data: currentData,
          lastKey: cursor,
        });
      } finally {
        if (refresh) {
          setIsRefreshing(false);
        }
      }
    },
  );

  const fetch = usePersistFn(async (...params: U[]) => {
    if (state.loading) return;
    await queryData(
      params,
      state.data,
      undefined,
      (_, results) => results,
      true,
    );
  });

  const fetchWithoutRefresh = usePersistFn(async (...params: U[]) => {
    if (state.loading) return;
    await queryData(
      params,
      state.data,
      undefined,
      (_, results) => results,
      false,
    );
  });

  const replace = usePersistFn(async (...params: U[]) => {
    requestId.current = Math.random();
    await queryData(
      params,
      [],
      undefined,
      (_, results) => results,
      false,
      requestId.current,
    );
  });

  const fetchMore = usePersistFn(async (...params: U[]) => {
    if (!hasMore || state.loading) return;
    await queryData(params, state.data, nextKey, (x, y) =>
      combineData(x, y, keyExtractor),
    );
  });

  const fetchSinceLastError = usePersistFn(async (...params: U[]) => {
    if (state.lastKey == null) return;
    await queryData(params, state.data, state.lastKey, (x, y) =>
      combineData(x, y, keyExtractor),
    );
  });

  const update = usePersistFn(
    (handler: (item: Draft<T>) => undefined | 'remove') => {
      const updatedState = produce(state, draft => {
        for (let i = draft.data.length - 1; i >= 0; i--) {
          const item = draft.data[i];
          const res = handler(item);
          if (res === 'remove') {
            draft.data.splice(i, 1);
          }
        }
      });
      if (updatedState !== state) {
        setState(updatedState);
      }
    },
  );

  return {
    fetch,
    fetchWithoutRefresh,
    replace,
    fetchMore,
    fetchSinceLastError,
    data: state.data,
    update,
    status: {
      nextKey,
      limit,
      isRefreshing,
      isLoading: state.loading,
      error: state.error,
      hasMore,
      loadedDataLength: state.data.length,
    },
  };
};
